"use client";

// Horizontal Scroll-Pinning für die „So läuft der Start"-Sektion.
// Desktop: Sektion wird gepinnt, vertikales Scrollen treibt horizontale Translation der Cards.
// Mobile: Fallback auf einfachen vertikalen Stack (Pinning auf Mobile ist meist störend).

import { useEffect, useRef, useState } from "react";
import {
  Target,
  Search,
  TrendingUp,
  Workflow,
  type LucideIcon,
} from "lucide-react";

type Step = {
  num: string;
  icon: LucideIcon;
  title: string;
  body: string;
  illustration: (props: { active: boolean }) => React.ReactNode;
};

const STEPS: Step[] = [
  {
    num: "01",
    icon: Target,
    title: "Bereich wählen",
    body: "Wir starten nicht mit allem gleichzeitig, sondern mit dem Bereich, in dem heute am meisten Zeit verloren geht, Qualität leidet oder Aktualität gebremst wird.",
    illustration: ({ active }) => <IllChoose active={active} />,
  },
  {
    num: "02",
    icon: Search,
    title: "Ist-Ablauf sichtbar machen",
    body: "Wir schauen auf die Realität: Tools, Übergaben, Wartezeiten, Doppelarbeit und Schnittstellen. Nicht auf die Idealversion, sondern auf den echten Prozess.",
    illustration: ({ active }) => <IllFlow active={active} />,
  },
  {
    num: "03",
    icon: TrendingUp,
    title: "ROI- und Zielbild schärfen",
    body: "Gemeinsam klären wir, wo der erste Flow messbar Wirkung entfalten soll — in Zeit, Prozessqualität, Transparenz, Aktualität oder Reichweite.",
    illustration: ({ active }) => <IllTarget active={active} />,
  },
  {
    num: "04",
    icon: Workflow,
    title: "Ersten Orchestra-Flow definieren",
    body: "Am Ende steht kein abstraktes Konzept, sondern ein konkreter erster Orchestra-Flow — aufgebaut auf eurem Ist-Ablauf und anschlussfähig für den nächsten Schritt.",
    illustration: ({ active }) => <IllNetwork active={active} />,
  },
];

/* ─────────── Mini-Illustrationen (monoline, scroll-animiert) ─────────── */

// Gemeinsame Stil-Props für alle Strokes
const STROKE_PROPS = {
  stroke: "currentColor",
  strokeWidth: 1.25,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  fill: "none",
  pathLength: 1 as const,
};

// Hilfs-Animation: Strich zeichnet sich beim active
function animStyle(active: boolean, delay = 0) {
  return {
    strokeDasharray: 1,
    strokeDashoffset: active ? 0 : 1,
    transition: `stroke-dashoffset 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  };
}
function fadeStyle(active: boolean, delay = 0) {
  return {
    opacity: active ? 1 : 0,
    transform: active ? "scale(1)" : "scale(0.8)",
    transformOrigin: "center",
    transition: `opacity 500ms ease ${delay}ms, transform 500ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  };
}

// 01 — Drei Optionen, eine gewählt
function IllChoose({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 200 100"
      className="w-full h-full text-[var(--color-blue)]"
      aria-hidden
    >
      {/* 3 Option-Kreise */}
      <circle cx="50" cy="50" r="16" {...STROKE_PROPS} style={animStyle(active, 0)} />
      <circle cx="100" cy="50" r="20" {...STROKE_PROPS} style={animStyle(active, 150)} />
      <circle cx="150" cy="50" r="16" {...STROKE_PROPS} style={animStyle(active, 300)} />
      {/* Gewählter Punkt in der Mitte */}
      <circle cx="100" cy="50" r="6" fill="currentColor" style={fadeStyle(active, 650)} />
      {/* Labels / Tick-Marks auf anderen */}
      <line x1="50" y1="74" x2="50" y2="80" {...STROKE_PROPS} style={animStyle(active, 400)} />
      <line x1="150" y1="74" x2="150" y2="80" {...STROKE_PROPS} style={animStyle(active, 450)} />
    </svg>
  );
}

// 02 — Fluss-Graph: 4 Boxes mit Pfeilen
function IllFlow({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 200 100"
      className="w-full h-full text-[var(--color-blue)]"
      aria-hidden
    >
      {/* Box 1 */}
      <rect x="10" y="20" width="34" height="22" rx="3" {...STROKE_PROPS} style={animStyle(active, 0)} />
      {/* Box 2 */}
      <rect x="66" y="20" width="34" height="22" rx="3" {...STROKE_PROPS} style={animStyle(active, 100)} />
      {/* Box 3 — unten links */}
      <rect x="38" y="60" width="34" height="22" rx="3" {...STROKE_PROPS} style={animStyle(active, 250)} />
      {/* Box 4 — unten rechts */}
      <rect x="110" y="60" width="34" height="22" rx="3" {...STROKE_PROPS} style={animStyle(active, 400)} />
      {/* Verbindungslinien */}
      <path d="M 44 31 L 66 31" {...STROKE_PROPS} style={animStyle(active, 550)} />
      <path d="M 83 42 C 83 55, 55 50, 55 60" {...STROKE_PROPS} style={animStyle(active, 650)} />
      <path d="M 100 31 C 130 31, 127 50, 127 60" {...STROKE_PROPS} style={animStyle(active, 750)} />
      {/* Kleine Pfeilspitzen am Ende */}
      <path d="M 64 29 L 66 31 L 64 33" {...STROKE_PROPS} style={animStyle(active, 850)} />
      <path d="M 53 58 L 55 60 L 57 58" {...STROKE_PROPS} style={animStyle(active, 900)} />
      <path d="M 125 58 L 127 60 L 129 58" {...STROKE_PROPS} style={animStyle(active, 950)} />
    </svg>
  );
}

// 03 — Ziel: aufsteigende Kurve mit Zielpunkt
function IllTarget({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 200 100"
      className="w-full h-full text-[var(--color-blue)]"
      aria-hidden
    >
      {/* Basis-Linie */}
      <line x1="20" y1="85" x2="180" y2="85" {...STROKE_PROPS} style={animStyle(active, 0)} opacity="0.35" />
      {/* Aufsteigende Kurve */}
      <path
        d="M 20 82 Q 70 80, 100 55 T 170 22"
        {...STROKE_PROPS}
        style={animStyle(active, 200)}
        strokeWidth={1.5}
      />
      {/* Mess-Ticks unten */}
      <line x1="20" y1="85" x2="20" y2="90" {...STROKE_PROPS} style={animStyle(active, 100)} />
      <line x1="60" y1="85" x2="60" y2="90" {...STROKE_PROPS} style={animStyle(active, 150)} />
      <line x1="100" y1="85" x2="100" y2="90" {...STROKE_PROPS} style={animStyle(active, 200)} />
      <line x1="140" y1="85" x2="140" y2="90" {...STROKE_PROPS} style={animStyle(active, 250)} />
      <line x1="180" y1="85" x2="180" y2="90" {...STROKE_PROPS} style={animStyle(active, 300)} />
      {/* Zielpunkt am Ende */}
      <circle cx="170" cy="22" r="8" {...STROKE_PROPS} style={animStyle(active, 900)} />
      <circle cx="170" cy="22" r="3" fill="currentColor" style={fadeStyle(active, 1100)} />
    </svg>
  );
}

// 04 — Orchestra-Flow: zentraler Node verzweigt zu mehreren
function IllNetwork({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 200 100"
      className="w-full h-full text-[var(--color-blue)]"
      aria-hidden
    >
      {/* Zentraler Node */}
      <circle cx="40" cy="50" r="8" fill="currentColor" style={fadeStyle(active, 0)} />
      {/* Ziel-Nodes */}
      <circle cx="150" cy="22" r="6" {...STROKE_PROPS} style={animStyle(active, 700)} />
      <circle cx="170" cy="50" r="6" {...STROKE_PROPS} style={animStyle(active, 800)} />
      <circle cx="150" cy="78" r="6" {...STROKE_PROPS} style={animStyle(active, 900)} />
      {/* Verbindungskanten */}
      <path d="M 48 47 Q 90 25, 144 22" {...STROKE_PROPS} style={animStyle(active, 250)} />
      <path d="M 48 50 L 164 50" {...STROKE_PROPS} style={animStyle(active, 400)} />
      <path d="M 48 53 Q 90 75, 144 78" {...STROKE_PROPS} style={animStyle(active, 550)} />
      {/* Feine Punkte auf den Pfaden (Datenfluss-Andeutung) */}
      <circle cx="95" cy="36" r="1.5" fill="currentColor" style={fadeStyle(active, 1000)} />
      <circle cx="105" cy="50" r="1.5" fill="currentColor" style={fadeStyle(active, 1100)} />
      <circle cx="95" cy="64" r="1.5" fill="currentColor" style={fadeStyle(active, 1200)} />
    </svg>
  );
}

export function ProcessTimeline() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  if (isMobile) return <VerticalFallback />;
  return <HorizontalPinned />;
}

/* ─────────── Desktop — Horizontal Scroll-Pinning ─────────── */
function HorizontalPinned() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0); // 0..1 durch die gepinnte Sektion

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Buffer-Zonen: Anfang und Ende halten Card 1 / Card 4 stabil,
    // damit die letzte Karte nach dem Eintreffen länger sichtbar bleibt.
    // Kombiniert mit längerer Sektion (5*100vh) gibt das ordentlich Luft am Ende.
    const START_BUFFER = 0.08; // 8% — Card 1 kurz halten
    const END_BUFFER = 0.3;    // 30% — Card 4 deutlich länger stehen lassen
    const usableRange = 1 - START_BUFFER - END_BUFFER;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const vh = window.innerHeight;
        const pinDistance = rect.height - vh;
        if (pinDistance <= 0) {
          setProgress(0);
          return;
        }
        const scrolled = Math.max(0, Math.min(pinDistance, -rect.top));
        const rawProgress = scrolled / pinDistance;
        // Remap: Buffer am Anfang, Übergänge in der Mitte, Buffer am Ende
        const adjusted = Math.max(
          0,
          Math.min(1, (rawProgress - START_BUFFER) / usableRange),
        );
        setProgress(adjusted);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Card-Geometrie in vw — quadratischere Proportion, enger Abstand
  const cardWidthVw = 44;
  const gapVw = 2.5;
  const stepDistance = cardWidthVw + gapVw;
  const totalTravel = (STEPS.length - 1) * stepDistance;
  // progress=0 → erste Card zentriert ((100-cardWidthVw)/2 = 28vw)
  // progress=1 → letzte Card zentriert
  const translateX = 28 - progress * totalTravel;

  // Welcher Step ist aktuell im Fokus? Sanfter Übergang als float.
  const activeFloat = progress * (STEPS.length - 1);

  return (
    <div
      ref={sectionRef}
      className="relative mt-16"
      style={{ height: `${(STEPS.length + 1) * 100}vh` }}
    >
      <div className="sticky top-0 h-screen overflow-hidden bg-[var(--color-bg)] flex flex-col justify-center">
        {/* Header darüber — bleibt sichtbar während des Pinnings */}
        <div className="absolute top-16 left-0 right-0 flex justify-center pointer-events-none">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--color-ink-muted)]">
            Durch die Schritte scrollen
          </div>
        </div>

        {/* Horizontal-Track */}
        <div
          className="flex items-center will-change-transform"
          style={{
            gap: `${gapVw}vw`,
            transform: `translateX(${translateX}vw)`,
          }}
        >
          {STEPS.map((step, i) => {
            const distance = Math.abs(activeFloat - i);
            const isNear = distance < 0.5;
            const opacity = Math.max(0.25, 1 - distance * 0.55);
            const scale = Math.max(0.88, 1 - distance * 0.08);

            return (
              <StepCard
                key={step.num}
                step={step}
                widthVw={cardWidthVw}
                opacity={opacity}
                scale={scale}
                isNear={isNear}
              />
            );
          })}
        </div>

        {/* Progress-Indikator unten */}
        <div className="absolute bottom-16 left-0 right-0 flex justify-center">
          <div className="flex items-center gap-4">
            {STEPS.map((s, i) => {
              const diff = activeFloat - i;
              const fill =
                diff >= 1 ? 1 : diff < 0 ? 0 : Math.max(0, Math.min(1, diff));
              return (
                <div key={s.num} className="flex items-center gap-4">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="font-mono text-[11px] tracking-[0.18em] transition-colors duration-300"
                      style={{
                        color:
                          i <= Math.round(activeFloat)
                            ? "var(--color-blue)"
                            : "var(--color-ink-muted)",
                      }}
                    >
                      {s.num}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="w-16 h-px bg-[var(--color-line)] overflow-hidden">
                      <div
                        className="h-full bg-[var(--color-blue)] origin-left"
                        style={{
                          transform: `scaleX(${fill})`,
                          transition: "transform 120ms linear",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({
  step,
  widthVw,
  opacity,
  scale,
  isNear,
}: {
  step: Step;
  widthVw: number;
  opacity: number;
  scale: number;
  isNear: boolean;
}) {
  const Icon = step.icon;
  // Illustration soll sich nur einmal zeichnen und dann stehen bleiben
  const [hasActivated, setHasActivated] = useState(false);
  useEffect(() => {
    if (isNear) setHasActivated(true);
  }, [isNear]);
  return (
    <div
      className="shrink-0 flex items-center justify-center"
      style={{ width: `${widthVw}vw` }}
    >
      <div
        className="relative bg-white rounded-[28px] border border-[var(--color-line)] w-full max-w-[640px] aspect-[5/6] overflow-hidden flex flex-col"
        style={{
          opacity,
          transform: `scale(${scale})`,
          transition: "opacity 300ms ease-out, transform 400ms ease-out, box-shadow 400ms ease-out",
          boxShadow: isNear
            ? "0 40px 80px -24px rgba(10,10,15,0.18)"
            : "0 12px 32px -12px rgba(10,10,15,0.08)",
        }}
      >
        {/* Grosse Serif-Nummer als Editorial-Ornament, rechts oben */}
        <div
          aria-hidden
          className="absolute -right-4 -top-8 font-serif italic text-[clamp(140px,15vw,240px)] leading-[0.9] text-[var(--color-blue)]/[0.07] select-none"
        >
          {step.num}
        </div>

        <div className="relative flex-1 flex flex-col p-10 md:p-12">
          {/* TOP — Icon + Mono-Nummer (Systematik) */}
          <div>
            <div className="w-14 h-14 rounded-full bg-[var(--color-blue)]/10 flex items-center justify-center mb-5">
              <Icon size={22} strokeWidth={1.6} className="text-[var(--color-blue)]" />
            </div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--color-blue)]">
              Schritt {step.num} von {String(STEPS.length).padStart(2, "0")}
            </div>
          </div>

          {/* MITTE — Mini-Illustration, zeichnet sich bei aktivem Card */}
          <div className="my-auto flex items-center justify-center py-6">
            <div className="w-full max-w-[380px] aspect-[2/1]">
              {step.illustration({ active: hasActivated })}
            </div>
          </div>

          {/* BOTTOM — Headline + Body, an den unteren Card-Rand gedrückt */}
          <div>
            <h3 className="font-serif text-[clamp(28px,2.4vw,42px)] leading-[1.1] tracking-[-0.01em] text-[var(--color-ink)] mb-5">
              {step.title}
            </h3>
            <p className="text-[clamp(14.5px,1vw,16.5px)] leading-[1.6] text-[var(--color-ink-soft)]">
              {step.body}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Mobile — Einfacher vertikaler Stack ─────────── */
function VerticalFallback() {
  return (
    <div className="mt-12 space-y-6">
      {STEPS.map((step) => {
        const Icon = step.icon;
        return (
          <div
            key={step.num}
            className="relative bg-white rounded-2xl border border-[var(--color-line)] p-8 overflow-hidden"
          >
            {/* Grosse Serif-Nummer als Ornament */}
            <div
              aria-hidden
              className="absolute -right-2 -top-4 font-serif italic text-[120px] leading-[0.9] text-[var(--color-blue)]/[0.06] select-none"
            >
              {step.num}
            </div>

            <div className="relative w-12 h-12 rounded-full bg-[var(--color-blue)]/10 flex items-center justify-center mb-5">
              <Icon size={20} className="text-[var(--color-blue)]" />
            </div>

            <div className="relative font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--color-blue)] mb-3">
              Schritt {step.num} von {String(STEPS.length).padStart(2, "0")}
            </div>

            <h3 className="relative font-serif text-[24px] leading-[1.2] mb-3 text-[var(--color-ink)]">
              {step.title}
            </h3>

            <p className="relative text-[15px] leading-[1.55] text-[var(--color-ink-soft)]">
              {step.body}
            </p>
          </div>
        );
      })}
    </div>
  );
}
